import { action, makeObservable, observable } from 'mobx'
import { PaginationBody } from '@Entities/pagination'
import { MultiStepStore } from '@App/stores/multi-step-store'

export enum LeavePageSource {
    BackButton = 'BACK_BUTTON',
    Navigation = 'NAVIGATION',
    None = 'NONE',
}

/**
 * A generic bulk action store. TAction should be an enum
 * describing available bulk actions for the store this is implemented in.
 * TSteps is an optional enum that provides multistep functionality if needed.
 * maxSelectionCount is an optional cap on how many ids can be selected for
 * bulk action at once. When undefined there is no cap.
 */
export abstract class BulkActionStore<
    TActions extends string,
    TStep extends string = never,
> extends MultiStepStore<TStep> {
    protected constructor(steps: TStep[] = [], maxSelectionCount?: number) {
        super(steps)
        this._maxSelectionCount = maxSelectionCount
        makeObservable<
            BulkActionStore<TActions, TStep>,
            | '_idsSelectedForBulkAction'
            | '_activeBulkAction'
            | '_isLeavePageModalOpen'
            | '_pendingPagination'
            | '_leavePageSource'
            | '_areFilesStaged'
        >(this, {
            _idsSelectedForBulkAction: observable,
            _activeBulkAction: observable,
            _isLeavePageModalOpen: observable,
            _pendingPagination: observable,
            _leavePageSource: observable,
            _areFilesStaged: observable,
            toggleSelectionForBulkAction: action,
            addMultipleToSelection: action,
            removeMultipleFromSelection: action,
            setActiveBulkAction: action,
            setLeavePageModalOpen: action,
            setLeavePageModalClosed: action,
            setAreFilesStaged: action,
            setPendingPagination: action,
            resetSelection: action,
            resetStore: action,
        })
    }

    protected _idsSelectedForBulkAction: Set<number> = new Set<number>()

    private readonly _maxSelectionCount?: number

    private _activeBulkAction: TActions | null = null

    private _isLeavePageModalOpen = false

    private _pendingPagination?: PaginationBody

    private _leavePageSource: LeavePageSource = LeavePageSource.None

    private _areFilesStaged = false

    get idsSelectedForBulkAction(): Set<number> {
        return this._idsSelectedForBulkAction
    }

    get activeBulkAction(): TActions | null {
        return this._activeBulkAction
    }

    get isLeavePageModalOpen(): boolean {
        return this._isLeavePageModalOpen
    }

    get leavePageSource(): LeavePageSource {
        return this._leavePageSource
    }

    get pendingPagination(): PaginationBody | undefined {
        return this._pendingPagination
    }

    get areFilesStaged(): boolean {
        return this._areFilesStaged
    }

    get maxSelectionCount(): number | undefined {
        return this._maxSelectionCount
    }

    get isSelectionCapReached(): boolean {
        return this._maxSelectionCount !== undefined && this._idsSelectedForBulkAction.size >= this._maxSelectionCount
    }

    setActiveBulkAction(newAction: TActions | null, preventReset?: boolean): void {
        this._activeBulkAction = newAction
        if (newAction === null && !preventReset) {
            this.resetSelection()
        }
    }

    setLeavePageModalOpen(pendingPagination?: PaginationBody, leavePageSource: LeavePageSource = LeavePageSource.None) {
        this._isLeavePageModalOpen = true
        this._pendingPagination = pendingPagination
        this._leavePageSource = leavePageSource
    }

    setLeavePageModalClosed() {
        this._isLeavePageModalOpen = false
    }

    setAreFilesStaged(areFilesStaged: boolean) {
        this._areFilesStaged = areFilesStaged
    }

    setPendingPagination(pagination?: PaginationBody) {
        this._pendingPagination = pagination
    }

    toggleSelectionForBulkAction(id?: number | null): void {
        if (id) {
            if (this._idsSelectedForBulkAction.has(id)) {
                this._idsSelectedForBulkAction.delete(id)
            } else if (!this.isSelectionCapReached) {
                this._idsSelectedForBulkAction.add(id)
            }
        }
    }

    addMultipleToSelection(ids: number[]): void {
        const combined = new Set([...this._idsSelectedForBulkAction, ...ids])
        this._idsSelectedForBulkAction =
            this._maxSelectionCount !== undefined && combined.size > this._maxSelectionCount
                ? new Set([...combined].slice(0, this._maxSelectionCount))
                : combined
    }

    removeMultipleFromSelection(ids: number[]): void {
        this._idsSelectedForBulkAction = new Set([...this._idsSelectedForBulkAction].filter((id) => !ids.includes(id)))
    }

    resetSelection(): void {
        this._idsSelectedForBulkAction = new Set<number>()
    }

    resetStore(): void {
        this.setActiveBulkAction(null)
        this._isLeavePageModalOpen = false
        this._leavePageSource = LeavePageSource.None
        this._pendingPagination = undefined
        this._areFilesStaged = false
        this.resetSelection()
        this.resetStep()
    }
}
